import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from '@/contexts/AuthContext';
import { useAuth } from '@/hooks/useAuth';
import { DashboardLayout } from '@/components/layout/DashboardLayout';

// Login
import LoginPage from '@/components/pages/LoginPage';

// Existing pages
import DashboardOverview from '@/components/pages/DashboardOverview';
import UploadDatabase from '@/components/pages/UploadDatabase';
import ProcessingJobs from '@/components/pages/ProcessingJobs';
import ResultsManagement from '@/components/pages/ResultsManagement';
import SuccessAnalytics from '@/components/pages/SuccessAnalytics';
import CreditBalance from '@/components/pages/CreditBalance';
import PurchaseCredits from '@/components/pages/PurchaseCredits';
import ApiKeys from '@/components/pages/ApiKeys';
import PlaceholderPage from '@/components/pages/PlaceholderPage';

// CMM — Claimant Management Module
import PipelineBoard from '@/components/pages/claimants/PipelineBoard';
import ClaimantSpreadsheet from '@/components/pages/claimants/ClaimantSpreadsheet';
import ClaimantDetailCard from './components/pages/claimants/ClaimantDetailCard';
import OverlapReviewDashboard from '@/components/pages/claimants/OverlapReviewDashboard';
import TemplateManagementPage from '@/components/pages/admin/TemplateManagementPage';
import PipelineStageConfigPage from '@/components/pages/admin/PipelineStageConfigPage';
import FirmUserManagementPage from '@/components/pages/admin/FirmUserManagementPage';
import NotificationSettingsPage from '@/components/pages/admin/NotificationSettingsPage';

// Reports
import ClientOverlapReport from './components/pages/reports/ClientOverlapReport';

// ProtectedRoute — redirects to /login if not authenticated
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <>{children}</>;
};

// Wraps any page element in ProtectedRoute + DashboardLayout
const P = (page: React.ReactNode) => (
  <ProtectedRoute>
    <DashboardLayout>{page}</DashboardLayout>
  </ProtectedRoute>
);

// LoginGuard — if already authenticated, redirect to dashboard
const LoginGuard: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return null;
  if (isAuthenticated) return <Navigate to="/claims/upload" replace />;
  return <LoginPage />;
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public route */}
          <Route path="/login" element={<LoginGuard />} />

          {/* Root redirect */}
          <Route path="/" element={<Navigate to="/claims/upload" replace />} />

          {/* Dashboard */}
          <Route path="/dashboard"           element={P(<DashboardOverview />)} />
          <Route path="/dashboard/activity"  element={P(<PlaceholderPage title="Recent Activity" section="Dashboard" />)} />
          <Route path="/dashboard/analytics" element={P(<PlaceholderPage title="Analytics" section="Dashboard" />)} />

          {/* Claims Discovery */}
          <Route path="/claims/upload"      element={P(<UploadDatabase />)} />
          <Route path="/claims/processing"  element={P(<ProcessingJobs />)} />
          <Route path="/claims/results"     element={P(<ResultsManagement />)} />
          <Route path="/claims/analytics"   element={P(<SuccessAnalytics />)} />

          {/* API Integration */}
          <Route path="/api/keys"          element={P(<ApiKeys />)} />
          <Route path="/api/documentation" element={P(<PlaceholderPage title="API Documentation" section="API Integration" />)} />
          <Route path="/api/testing"       element={P(<PlaceholderPage title="Testing Console" section="API Integration" />)} />

          {/* Account & Billing */}
          <Route path="/account/credits"  element={P(<CreditBalance />)} />
          <Route path="/account/purchase" element={P(<PurchaseCredits />)} />
          <Route path="/account/usage"    element={P(<PlaceholderPage title="Usage History" section="Account & Billing" />)} />
          <Route path="/account/billing"  element={P(<PlaceholderPage title="Billing Records" section="Account & Billing" />)} />
          <Route path="/account/payment"  element={P(<PlaceholderPage title="Payment Methods" section="Account & Billing" />)} />

          {/* Support & Resources */}
          <Route path="/support/guide"     element={P(<PlaceholderPage title="User Guide" section="Support & Resources" />)} />
          <Route path="/support/api"       element={P(<PlaceholderPage title="API Documentation" section="Support & Resources" />)} />
          <Route path="/support/tutorials" element={P(<PlaceholderPage title="Video Tutorials" section="Support & Resources" />)} />
          <Route path="/support/contact"   element={P(<PlaceholderPage title="Contact Support" section="Support & Resources" />)} />

          {/* CMM — Claimant Management (client routes) */}
          <Route path="/claimants/pipeline"    element={P(<PipelineBoard />)} />
          <Route path="/claimants/spreadsheet" element={P(<ClaimantSpreadsheet />)} />
          <Route path="/claimants/:id"         element={P(<ClaimantDetailCard />)} />
          <Route path="/claimants/add"         element={P(<ClaimantSpreadsheet />)} />
          <Route path="/claimants/overlaps"    element={P(<OverlapReviewDashboard />)} />

          {/* Reports */}
          <Route path="/reports/client-overlap" element={P(<ClientOverlapReport />)} />

          {/* CMM — Admin routes */}
          <Route path="/admin/templates"       element={P(<TemplateManagementPage />)} />
          <Route path="/admin/pipeline-stages" element={P(<PipelineStageConfigPage />)} />
          <Route path="/admin/firm-users"      element={P(<FirmUserManagementPage />)} />
          <Route path="/admin/notifications"   element={P(<NotificationSettingsPage />)} />

          {/* Fallback — must be last */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
};

export default App;